import React, { useState, useEffect } from 'react';
import '../../styles/AutomationCard.css';
import { Button, IconButton, Input, Dropdown, ExitScreen } from '../';
import { Exit } from '../../assets/icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Edit02Icon } from '@hugeicons/core-free-icons';
import { colour } from '../../styles/GlobalStyles';
import { setAutomation } from '../../functions';

function UnderwritingSection(props) {
    const { automation, onUpdate, companyId, automationId } = props;
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [repairCostTemplateDropdownOpen, setRepairCostTemplateDropdownOpen] = useState(false);
    
    // Form state
    const [formRepairCostTemplate, setFormRepairCostTemplate] = useState(2); // 1=price per sqft, 2=bands, 3=rehab levels
    const [formAnnualInterestRate, setFormAnnualInterestRate] = useState('12');
    const [formHoldMonths, setFormHoldMonths] = useState('4');
    const [formTitleRate, setFormTitleRate] = useState('1.5');
    const [formCommissionRate, setFormCommissionRate] = useState('4');
    const [formPointsRate, setFormPointsRate] = useState('2');
    const [formDownPayment, setFormDownPayment] = useState('0');
    
    // Initialize form state
    useEffect(() => {
        if (editMode && automation) {
            // Repair cost template: 1=price per sqft, 2=bands, 3=rehab levels
            const repairCostTemplate = automation.underwriting?.repairCostTemplate !== undefined && automation.underwriting?.repairCostTemplate !== null 
                ? Number(automation.underwriting.repairCostTemplate) 
                : 2; // Default to bands
            setFormRepairCostTemplate(repairCostTemplate);
            
            // Underwriting values (use automation values or defaults)
            const underwritingValues = automation.underwriting?.values || {};
            
            setFormAnnualInterestRate(underwritingValues.annualInterestRate !== undefined 
                ? (underwritingValues.annualInterestRate * 100).toString() 
                : '12');
            setFormHoldMonths(underwritingValues.holdMonths !== undefined 
                ? underwritingValues.holdMonths.toString() 
                : '4');
            setFormTitleRate(underwritingValues.titleRate !== undefined 
                ? (underwritingValues.titleRate * 100).toString() 
                : '1.5');
            setFormCommissionRate(underwritingValues.commissionRate !== undefined 
                ? (underwritingValues.commissionRate * 100).toString() 
                : '4');
            setFormPointsRate(underwritingValues.pointsRate !== undefined 
                ? (underwritingValues.pointsRate * 100).toString() 
                : '2');
            setFormDownPayment(underwritingValues.downPayment !== undefined 
                ? (underwritingValues.downPayment * 100).toString() 
                : '0');
            
            setHasChanges(false);
        }
    }, [editMode, automation]);
    
    // Check for changes
    useEffect(() => {
        if (editMode && automation) {
            const originalRepairCostTemplate = automation.underwriting?.repairCostTemplate !== undefined 
                ? Number(automation.underwriting.repairCostTemplate) 
                : 2;
            
            const originalUnderwriting = automation.underwriting?.values || {};
            const originalAnnualInterestRate = originalUnderwriting.annualInterestRate !== undefined 
                ? (originalUnderwriting.annualInterestRate * 100).toString() 
                : '12';
            const originalHoldMonths = originalUnderwriting.holdMonths !== undefined 
                ? originalUnderwriting.holdMonths.toString() 
                : '4';
            const originalTitleRate = originalUnderwriting.titleRate !== undefined 
                ? (originalUnderwriting.titleRate * 100).toString() 
                : '1.5';
            const originalCommissionRate = originalUnderwriting.commissionRate !== undefined 
                ? (originalUnderwriting.commissionRate * 100).toString() 
                : '4';
            const originalPointsRate = originalUnderwriting.pointsRate !== undefined 
                ? (originalUnderwriting.pointsRate * 100).toString() 
                : '2';
            const originalDownPayment = originalUnderwriting.downPayment !== undefined 
                ? (originalUnderwriting.downPayment * 100).toString() 
                : '0';
            
            const isMissingValue = (value) => (
                value === undefined ||
                value === null ||
                (typeof value === 'string' && value.trim() === '') ||
                (Array.isArray(value) && value.length === 0) ||
                (typeof value === 'object' && !Array.isArray(value) && value && Object.keys(value).length === 0)
            );

            const storedRepairCostTemplate = automation?.underwriting?.repairCostTemplate;
            const hasMissingUnderwritingValue = [
                'annualInterestRate',
                'holdMonths',
                'titleRate',
                'commissionRate',
                'pointsRate',
                'downPayment'
            ].some((key) => isMissingValue(originalUnderwriting?.[key]));

            const hasUnsavedDefaults = isMissingValue(storedRepairCostTemplate) || hasMissingUnderwritingValue;

            const changed = 
                formRepairCostTemplate !== originalRepairCostTemplate ||
                formAnnualInterestRate !== originalAnnualInterestRate ||
                formHoldMonths !== originalHoldMonths ||
                formTitleRate !== originalTitleRate ||
                formCommissionRate !== originalCommissionRate ||
                formPointsRate !== originalPointsRate ||
                formDownPayment !== originalDownPayment;

            setHasChanges(changed || hasUnsavedDefaults);
        }
    }, [formRepairCostTemplate, formAnnualInterestRate, formHoldMonths, formTitleRate, formCommissionRate, formPointsRate, formDownPayment, editMode, automation]);

    // Get analysis text
    const getAnalysisText = () => {
        const selectedTemplate = automation?.underwriting?.repairCostTemplate;
        const underwritingValues = automation?.underwriting?.values || automation?.underwriting || {};

        const parts = [];
        if (selectedTemplate !== undefined && selectedTemplate !== null) {
            const templateNumber = Number(selectedTemplate);
            const templateText = getTemplateText(templateNumber);
            if (!Number.isNaN(templateNumber)) {
                parts.push(`Template #${templateNumber}: ${templateText}`);
            }
        }

        const formatPercent = (value) => {
            if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
            const percent = Number(value) * 100;
            return Number.isInteger(percent) ? `${percent}%` : `${percent.toFixed(2)}%`;
        };

        const annualInterestRate = formatPercent(underwritingValues.annualInterestRate);
        if (annualInterestRate) parts.push(`${annualInterestRate} annual interest rate`);

        if (underwritingValues.holdMonths !== undefined && underwritingValues.holdMonths !== null) {
            const months = Number(underwritingValues.holdMonths);
            if (!Number.isNaN(months)) {
                parts.push(`${months} month${months === 1 ? '' : 's'} holding`);
            }
        }

        const titleRate = formatPercent(underwritingValues.titleRate);
        if (titleRate) parts.push(`${titleRate} title`);

        const commissionRate = formatPercent(underwritingValues.commissionRate);
        if (commissionRate) parts.push(`${commissionRate} commission`);

        const pointsRate = formatPercent(underwritingValues.pointsRate);
        if (pointsRate) parts.push(`${pointsRate} points`);

        const downPayment = formatPercent(underwritingValues.downPayment);
        if (downPayment) parts.push(`${downPayment} down`);

        if (parts.length > 0) {
            return parts.join(' • ');
        }

        return 'Calculate ARV and rehab estimate using the selected template.';
    };
    
    const getTemplateText = (templateNumber) => {
        switch(templateNumber) {
            case 1: return 'Price per sqft';
            case 2: return 'Bands';
            case 3: return 'Rehab Levels';
            default: return 'Bands';
        }
    };

    const handleCancel = () => {
        setEditMode(false);
        setHasChanges(false);
    };
    
    const handleSave = async () => {
        if (!companyId || !automationId || !hasChanges) return;
        
        setSaving(true);
        try {
            // Convert percentage strings to decimals for rates
            const annualInterestRate = parseFloat(formAnnualInterestRate) / 100;
            const titleRate = parseFloat(formTitleRate) / 100;
            const commissionRate = parseFloat(formCommissionRate) / 100;
            const pointsRate = parseFloat(formPointsRate) / 100;
            const downPayment = parseFloat(formDownPayment) / 100;
            const holdMonths = parseInt(formHoldMonths, 10);
            
            // Validate inputs
            if (isNaN(annualInterestRate) || annualInterestRate < 0 || annualInterestRate > 1) {
                alert('Annual interest rate must be between 0% and 100%');
                setSaving(false);
                return;
            }
            if (isNaN(holdMonths) || holdMonths < 0) {
                alert('Hold months must be a positive number');
                setSaving(false);
                return;
            }
            if (isNaN(titleRate) || titleRate < 0 || titleRate > 1) {
                alert('Title rate must be between 0% and 100%');
                setSaving(false);
                return;
            }
            if (isNaN(commissionRate) || commissionRate < 0 || commissionRate > 1) {
                alert('Commission rate must be between 0% and 100%');
                setSaving(false);
                return;
            }
            if (isNaN(pointsRate) || pointsRate < 0 || pointsRate > 1) {
                alert('Points rate must be between 0% and 100%');
                setSaving(false);
                return;
            }
            if (isNaN(downPayment) || downPayment < 0 || downPayment > 1) {
                alert('Down payment must be between 0% and 100%');
                setSaving(false);
                return;
            }
            
            const payload = {
                underwriting: {
                    repairCostTemplate: formRepairCostTemplate,
                    values: {
                        annualInterestRate,
                        holdMonths,
                        titleRate,
                        commissionRate,
                        pointsRate,
                        downPayment
                    }
                }
            };
            
            const result = await setAutomation(companyId, automationId, payload);
            if (result.status === 200) {
                if (onUpdate) {
                    onUpdate({ 
                        ...automation, 
                        underwriting: payload.underwriting
                    });
                }
                setEditMode(false);
                setHasChanges(false);
            } else {
                alert(result.message || 'Failed to save underwriting settings. Please try again.');
            }
        } catch (error) {
            console.error('Error saving underwriting:', error);
            alert('Failed to save underwriting settings. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const closeAllDropdowns = () => {
        setRepairCostTemplateDropdownOpen(false);
    };

    const underwritingValues = automation?.underwriting?.values || {};
    const hasUnderwritingValues = [
        'annualInterestRate',
        'holdMonths',
        'titleRate',
        'commissionRate',
        'pointsRate',
        'downPayment'
    ].some((key) => underwritingValues[key] !== undefined && underwritingValues[key] !== null);
    const hasRepairCostTemplate = automation?.underwriting?.repairCostTemplate !== undefined
        && automation?.underwriting?.repairCostTemplate !== null;
    const showUnderwritingHint = !hasRepairCostTemplate && !hasUnderwritingValues && !(editMode && hasChanges);

    return (
        <div className={`automation-form-section-card automation-detail-section-card ${editMode ? '' : 'automation-form-section-card-compact'}`}>
            {repairCostTemplateDropdownOpen && <ExitScreen clickScreen={closeAllDropdowns} />}
            
            <div className="automation-form-section-card-content">
                <div className="automation-form-section-card-text">
                    <span className="body-bold colour-primary">Underwriting</span>
                    {showUnderwritingHint && (
                        <span className="body-regular colour-secondary">
                            Customize how properties are analyzed.
                        </span>
                    )}
                </div>
                <div className="automation-form-section-card-icon-button">
                    <IconButton
                        size="medium"
                        icon={editMode ? <Exit fill={colour.red03} size={14} clickFunc={null} /> : <HugeiconsIcon icon={Edit02Icon} color={colour.gray06} size={16} />}
                        disabled={false}
                        clickFunc={editMode ? handleCancel : () => setEditMode(true)}
                        standardIcon={false}
                        background={true}
                        bgType={editMode ? "destructive" : "white"}
                    />
                </div>
            </div>
            
            {editMode ? (
                <div className="automation-underwriting-edit-panel">
                    <div className="automation-form-section">
                        <span className="body-bold colour-primary">
                            Repair costs
                        </span>
                        <Dropdown
                            open={repairCostTemplateDropdownOpen}
                            icon={null}
                            label={`Repair cost template: ${getTemplateText(formRepairCostTemplate)}`}
                            disabled={false}
                            options={[
                                { label: 'Price per sqft', value: 1, selected: formRepairCostTemplate === 1 },
                                { label: 'Bands', value: 2, selected: formRepairCostTemplate === 2 },
                                { label: 'Rehab Levels', value: 3, selected: formRepairCostTemplate === 3 }
                            ]}
                            multiselect={false}
                            cta={(item) => {
                                setFormRepairCostTemplate(item.value);
                                setRepairCostTemplateDropdownOpen(false);
                            }}
                            sidealign="left"
                            topalign="bottom"
                            toggleDropdown={() => setRepairCostTemplateDropdownOpen(!repairCostTemplateDropdownOpen)}
                        />
                    </div>
                    
                    <div className="automation-form-divider margin-tops"></div>
                    
                    <div className="automation-form-section">
                        <div className="automation-form-rule-grid">
                            <Input
                                label="Annual interest rate"
                                labelType="default"
                                info={false}
                                value={formAnnualInterestRate}
                                onChange={(e) => setFormAnnualInterestRate(e.target.value)}
                                placeholder="12"
                                leftUnitSelected={true}
                                type="text"
                                rightUnit="%"
                                unitSwitch={false}
                                error={false}
                            />
                            <Input
                                label="Hold months"
                                labelType="default"
                                info={false}
                                value={formHoldMonths}
                                leftUnitSelected={true}
                                onChange={(e) => setFormHoldMonths(e.target.value)}
                                placeholder="4"
                                type="text"
                                rightUnit="months"
                                unitSwitch={false}
                                error={false}
                            />
                            <Input
                                label="Title rate"
                                labelType="default"
                                info={false}
                                value={formTitleRate}
                                leftUnitSelected={true}
                                onChange={(e) => setFormTitleRate(e.target.value)}
                                placeholder="1.5"
                                type="text"
                                rightUnit="%"
                                unitSwitch={false}
                                error={false}
                            />
                            <Input
                                label="Commission rate"
                                labelType="default"
                                info={false}
                                value={formCommissionRate}
                                onChange={(e) => setFormCommissionRate(e.target.value)}
                                placeholder="4"
                                leftUnitSelected={true}
                                type="text"
                                rightUnit="%"
                                unitSwitch={false}
                                error={false}
                            />
                            <Input
                                label="Points rate"
                                labelType="default"
                                info={false}
                                value={formPointsRate}
                                onChange={(e) => setFormPointsRate(e.target.value)}
                                placeholder="2"
                                leftUnitSelected={true}
                                type="text"
                                rightUnit="%"
                                unitSwitch={false}
                                error={false}
                            />
                            <Input
                                label="Down payment"
                                labelType="default"
                                info={false}
                                value={formDownPayment}
                                onChange={(e) => setFormDownPayment(e.target.value)}
                                placeholder="0"
                                leftUnitSelected={true}
                                type="text"
                                rightUnit="%"
                                unitSwitch={false}
                                error={false}
                            />
                        </div>
                    </div>
                    
                    <div className="automation-form-footer">
                        <div className="automation-form-footer-button">
                            <Button
                                buttonType="primary-button"
                                size="large"
                                text="Save changes"
                                cta={handleSave}
                                disabled={!hasChanges || saving}
                                spinner={saving}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="automation-detail-section-summary">
                    <span className="body-regular colour-secondary">{getAnalysisText()}</span>
                </div>
            )}
        </div>
    );
}

export default UnderwritingSection;
